import React from 'react';

import { css, styled } from 'styled-components';

import theme from 'styles/theme';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import CtaButton from 'components/CtaButton/CtaButton';
import Flex, { FlexContainer } from 'components/Flex/Flex';
import WatchCard from 'components/ModularBlocks/WatchBlock/components/WatchCard';

import { WatchBlockI } from 'interfaces/content-stack/modular-blocks/watch';

import { autoOptimizeImage } from 'utils/functions';

export interface WatchBlockProps {
  data: WatchBlockI;
}

const VideoContainer = styled('div')(
  ({ theme }) => css`
    display: grid;
    grid-column-gap: 2rem;
    grid-row-gap: 2rem;
    grid-template-rows: auto auto;
    grid-template-columns: 2.25fr 1fr;
    grid-auto-columns: 1fr;

    @media (max-width: ${theme.breakpoints.lg}) {
      grid-template-columns: 1fr 1fr;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
    }
  `
);

const SmallVideoContainer = styled('div')(
  ({ theme }) => css`
    display: flex;
    height: 100%;
    min-width: 21rem;
    gap: 2rem;
    flex-direction: column;

    @media (max-width: ${theme.breakpoints.lg}) {
      gap: 1rem;
      min-width: 0;
    }
  `
);

const Container = styled('div')<{ $url?: string }>(
  ({ theme, $url }) => css`
    background-color: ${theme.colors.primaryBlack2};
    background-image: url(${$url});
    background-position: 0 0;
    background-repeat: repeat-y;
    background-size: cover;
    background-attachment: fixed;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;

    ${FlexContainer} {
      @media (max-width: ${theme.breakpoints.xs}) {
        flex-direction: column;
      }
    }
  `
);

const Title = styled('h4')(
  ({ theme }) => css`
    font-family: ${theme.fonts.fontFamilySecondary};
    font-weight: 500;
    margin: 0;
    font-size: 1.5rem;
    text-transform: uppercase;

    @media (max-width: ${theme.breakpoints.lg}) {
      font-size: 1.25rem;
    }
  `
);

const WatchBlock: React.FC<WatchBlockProps> = ({ data }) => {
  const { backgroundImage, header, cta } = data;
  const { url } = backgroundImage;
  const largeWatchCard = data.watchCards[0];
  const smallWatchCards = data.watchCards.slice(1);

  return (
    <Container $url={autoOptimizeImage(url) ?? url ?? ''} data-elastic-exclude>
      <ContentContainer>
        <Flex w100 gap={1} styles={{ color: theme.colors.white, marginBottom: '2rem' }}>
          <Title>{header}</Title>
          {cta?.buttonText && (
            <>
              |
              <CtaButton
                to={cta.buttonLink[0].linkUrl.href}
                buttonType='link'
                openInNewWindow={cta.buttonLink[0].openInNewWindow}
                text={cta.buttonText}
                textColor={theme.colors.white}
                dataLayerInfo={header}
              />
            </>
          )}
        </Flex>
        <VideoContainer>
          <WatchCard id={largeWatchCard.videoId} article={largeWatchCard.article[0]} />
          {smallWatchCards && (
            <SmallVideoContainer>
              {smallWatchCards.map((card) => (
                <WatchCard id={card.videoId} article={card.article[0]} key={card.videoId} size='sm' />
              ))}
            </SmallVideoContainer>
          )}
        </VideoContainer>
      </ContentContainer>
    </Container>
  );
};

export default WatchBlock;
